import { Stepper } from "@mantine/core";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { zodResolver } from "mantine-form-zod-resolver";
import { httpPostGraphql } from "shared/api/httpClient";
import { BackButton } from "shared/components/global/BackButton";
import { Content } from "shared/components/global/Content";
import {
    createIpawsAlertFormState,
    IpawsAlertFormProvider,
    ipawsAlertFormSteppedStateSchema,
    useIpawsAlertForm,
} from "shared/components/ipaws/alert/context";
import { IpawsAlertFormArea } from "shared/components/ipaws/alert/IpawsAlertFormArea";
import { IpawsAlertFormConfiguration } from "shared/components/ipaws/alert/IpawsAlertFormConfiguration";
import { IpawsAlertFormConnectionStatus } from "shared/components/ipaws/alert/IpawsAlertFormConnectionStatus";
import { IpawsAlertFormContent } from "shared/components/ipaws/alert/IpawsAlertFormContent";
import { IpawsAlertFormProfile } from "shared/components/ipaws/alert/IpawsAlertFormProfile";
import { IpawsAlertFormStepLayout } from "shared/components/ipaws/alert/IpawsAlertFormStepLayout";
import { useCustomer } from "shared/graphql/cust";
import { useFormState } from "shared/hooks/useFormState";
import { useIsMobile } from "shared/hooks/useIsMobile";
import { usePermissionCheck } from "shared/stores/oidc";
import { to } from "shared/utils/fns";
import { NoMessagesFigure } from "src/assets/NoMessagesFigure";
import { graphql } from "src/gql";
import { Permission } from "src/gql/graphql";
import { queryClient } from "src/queryClient";

const Page = () => {
    const hasIpawsView = usePermissionCheck(Permission.IpawsHistoryView);
    if (!hasIpawsView) return <NoMessagesFigure>You do not have permission to view this page</NoMessagesFigure>;

    const { id } = Route.useParams();
    const { data: customer } = useCustomer();
    const {
        data: { alert },
    } = useSuspenseQuery(query(id));

    const isMobile = useIsMobile();

    const initial = createIpawsAlertFormState(alert.capMessage, {
        mode: "new",
        customerId: customer.id,
        certId: alert.certificate.id,
        durationMinutes: alert.durationMinutes,
    });

    const form = useIpawsAlertForm({
        cascadeUpdates: true,
        initialValues: {
            ...initial,
            meta: {
                ...initial.meta,
                step: 3, // start at last step for sending
            },
        },
        validate: zodResolver(ipawsAlertFormSteppedStateSchema),
    });

    const [step, setStep] = useFormState(form, "meta.step");

    return (
        <IpawsAlertFormProvider form={form}>
            <Content paper>
                <Content.Heading>
                    <BackButton nav={{ to: "/ipaws/alerts" }} />
                    Edit IPAWS Alert Template
                </Content.Heading>
                <Content.Action>
                    <IpawsAlertFormConnectionStatus />
                </Content.Action>
                <Stepper active={step} onStepClick={setStep} size="xs" radius="sm" styles={{ steps: { display: isMobile ? "none" : "" } }}>
                    <Stepper.Step label="Step 1" description="Profile" allowStepSelect={step >= 0}>
                        <IpawsAlertFormProfile />
                    </Stepper.Step>
                    <Stepper.Step label="Step 2" description="Configuration" allowStepSelect={step >= 1}>
                        <IpawsAlertFormConfiguration />
                    </Stepper.Step>
                    <Stepper.Step label="Step 3" description="Geographic Area" allowStepSelect={step >= 2}>
                        <IpawsAlertFormArea />
                    </Stepper.Step>
                    <Stepper.Step label="Step 4" description="Message Content" allowStepSelect={step >= 3}>
                        <IpawsAlertFormStepLayout>
                            <IpawsAlertFormContent />
                        </IpawsAlertFormStepLayout>
                    </Stepper.Step>
                </Stepper>
            </Content>
        </IpawsAlertFormProvider>
    );
};

const query = (id: string) =>
    queryOptions({
        queryKey: ["ipaws", "templates", id],
        select: to("ipawsTemplate"),
        queryFn: () =>
            httpPostGraphql(
                graphql(`
                    query IpawsTemplateForSend($id: UUID!) {
                        ipawsTemplate(id: $id) {
                            alert {
                                id
                                certificate {
                                    id
                                }
                                capMessage {
                                    ...IpawsCapMessageDetails
                                }
                                durationMinutes
                            }
                        }
                    }
                `),
                { id },
            ),
    });

/** @public */
export const Route = createFileRoute("/_auth/ipaws/templates/send/$id")({
    component: Page,
    async beforeLoad({ params: { id } }) {
        await queryClient.ensureQueryData(query(id));
    },
});
